import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from './Modal';
import { Icon } from './ui';

// ── RevealKeyModal ─────────────────────────────────────────────────────────
// Shown exactly once, right after key creation. The backend only ever stores
// the SHA-256 hash, so if the operator dismisses this without copying the
// plain key, the key is unrecoverable — they must create a new one.
//
// Backdrop click / Escape are NOT enough to close it: we force the operator
// to click the explicit confirm button by passing a no-op `onClose` to the
// underlying Modal.
//
// The copy button looks like the kit's CopyButton but is local on purpose.
// CopyButton says nothing when the browser refuses the clipboard, and this is
// the one dialog where a copy that silently failed loses the key for good. So a
// refusal still raises a toast telling the operator to copy by hand, and the key
// wraps instead of truncating, so all of it is always there to select.

export default function RevealKeyModal({ apiKey, onClose }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!apiKey) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success('Copied');
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Clipboard not available, select and copy manually');
    }
  };

  return (
    <Modal
      open
      // Intentional no-op: the underlying Modal calls onClose on backdrop
      // click + Escape. We want the user to actively confirm they saved the
      // key, so swallow those.
      onClose={() => { /* require explicit confirm */ }}
      // No corner ✕ either. With the no-op above it drew a close button that
      // did nothing when pressed.
      showClose={false}
      title="Save this key now, it won't be shown again"
      maxWidth={600}
    >
      <div className="notice notice-warn">
        <Icon name="alert" />
        <div className="notice-body">
          <div className="notice-title">This is the only time the full key is displayed.</div>
          Store it in your producer&apos;s secret manager now. The backend keeps only a
          hash, so once you close this dialog the plain value is unrecoverable.
        </div>
      </div>

      <div className="form-label">API key</div>
      <div
        className="copy-field"
        style={{ height: 'auto', minHeight: 'var(--control-h)', paddingBlock: 'var(--sp-1)' }}
      >
        {/* Wraps rather than truncating, and one click selects all of it: that is
            the manual fallback the clipboard-refused toast sends the operator to. */}
        <span className="break" style={{ whiteSpace: 'normal', userSelect: 'all' }}>{apiKey}</span>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-icon"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy key'}
          title="Copy key"
        >
          <Icon name={copied ? 'check' : 'copy'} />
        </button>
      </div>
      <p className="form-hint">Click the key to select all of it.</p>

      <div className="modal-footer">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I have saved it, close
        </button>
      </div>
    </Modal>
  );
}
